import { z } from 'zod';

// src/grouping/model.ts
var GROUPING_COMPARATORS = [
  "eq",
  "neq",
  "in",
  "nin",
  "contains",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "missing"
];
var GROUPING_SCOPES = ["default", "org", "tenant", "user"];
var conditionSchema = z.object({
  /** Field path on a {@link TxFact}, e.g. `method`, `valueUsd`, `tags`. */
  field: z.string().trim().min(1),
  op: z.enum(GROUPING_COMPARATORS),
  /** Comparison operand. Omitted for `exists`/`missing`. */
  value: z.unknown().optional()
});
var stepSchema = z.object({
  id: z.string().trim().min(1),
  label: z.string().trim().optional(),
  /** All conditions must hold (AND). An empty list matches any transaction. */
  match: z.array(conditionSchema).default([])
});
var correlationSchema = z.object({
  /** Fields whose values must be equal across every matched tx in a group. */
  equalFields: z.array(z.string().trim().min(1)).optional()
});
var GroupingRuleSchema = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1, "Name is required").max(120),
  description: z.string().trim().max(2e3).optional(),
  enabled: z.boolean().default(true),
  scope: z.enum(GROUPING_SCOPES).default("user"),
  /** Ordered steps; a group must satisfy them in sequence. */
  steps: z.array(stepSchema).min(1, "At least one step is required"),
  correlation: correlationSchema.optional(),
  /** Max elapsed time (ms) between the first and last step. 0/omitted = unbounded. */
  windowMs: z.number().int().nonnegative().optional(),
  /** Require the matched steps to span more than one chain. */
  crossChain: z.boolean().default(false),
  /** Optional colour for the group badge/edge in the graph. */
  color: z.string().trim().optional()
});
function validateGroupingRule(rule) {
  const result = GroupingRuleSchema.safeParse(rule);
  if (result.success) return { success: true, data: result.data, fieldErrors: {} };
  const flat = result.error.flatten();
  return {
    success: false,
    fieldErrors: flat.fieldErrors,
    message: flat.formErrors[0]
  };
}
function approveThenSendRule(overrides = {}) {
  return GroupingRuleSchema.parse({
    id: overrides.id ?? "approve-then-send",
    name: overrides.name ?? "Approve then send",
    description: overrides.description ?? "An approval followed by a transfer from the same address \u2014 the classic two-step settlement pattern.",
    crossChain: overrides.crossChain ?? true,
    correlation: overrides.correlation ?? { equalFields: ["from"] },
    windowMs: overrides.windowMs,
    steps: overrides.steps ?? [
      { id: "approve", label: "Approve", match: [{ field: "method", op: "eq", value: "approve" }] },
      {
        id: "send",
        label: "Send",
        match: [{ field: "method", op: "in", value: ["transfer", "transferFrom", "send"] }]
      }
    ],
    ...overrides
  });
}

// src/grouping/evaluate.ts
function fieldValue(tx, field) {
  if (field in tx) return tx[field];
  if (field.includes(".")) {
    let cur = tx;
    for (const part of field.split(".")) {
      if (cur == null || typeof cur !== "object") return void 0;
      cur = cur[part];
    }
    return cur;
  }
  return void 0;
}
function asNumber(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
function compare(actual, op, expected) {
  switch (op) {
    case "exists":
      return actual != null;
    case "missing":
      return actual == null;
    case "eq":
      return actual === expected;
    case "neq":
      return actual !== expected;
    case "in":
      return Array.isArray(expected) && expected.includes(actual);
    case "nin":
      return Array.isArray(expected) && !expected.includes(actual);
    case "contains": {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === "string") return actual.includes(String(expected));
      return false;
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = asNumber(actual);
      const b = asNumber(expected);
      if (a === void 0 || b === void 0) return false;
      return op === "gt" ? a > b : op === "gte" ? a >= b : op === "lt" ? a < b : a <= b;
    }
    default:
      return false;
  }
}
function conditionHolds(tx, condition) {
  return compare(fieldValue(tx, condition.field), condition.op, condition.value);
}
function stepMatches(tx, step) {
  return step.match.every((c) => conditionHolds(tx, c));
}
function correlationKey(tx, equalFields) {
  if (!equalFields || equalFields.length === 0) return "";
  return equalFields.map((f) => `${f}=${String(fieldValue(tx, f) ?? "")}`).join("&");
}
function evaluateRule(rule, txs) {
  if (rule.enabled === false || rule.steps.length === 0) return [];
  const equalFields = rule.correlation?.equalFields;
  const partitions = /* @__PURE__ */ new Map();
  for (const tx of txs) {
    const key = correlationKey(tx, equalFields);
    let bucket = partitions.get(key);
    if (!bucket) {
      bucket = [];
      partitions.set(key, bucket);
    }
    bucket.push(tx);
  }
  const groups = [];
  for (const [key, partTxs] of partitions) {
    const sorted = [...partTxs].sort((a, b) => a.timestamp - b.timestamp);
    let current = [];
    let stepIdx = 0;
    const reset = () => {
      current = [];
      stepIdx = 0;
    };
    for (const tx of sorted) {
      if (current.length > 0 && rule.windowMs && rule.windowMs > 0) {
        const anchor = current[0].tx.timestamp;
        if (tx.timestamp - anchor > rule.windowMs) {
          reset();
        }
      }
      const step = rule.steps[stepIdx];
      if (stepMatches(tx, step)) {
        current.push({ stepId: step.id, tx });
        stepIdx += 1;
        if (stepIdx === rule.steps.length) {
          const group = finalizeGroup(rule, key, current);
          if (group) groups.push(group);
          reset();
        }
      } else if (stepIdx > 0 && stepMatches(tx, rule.steps[0])) {
        current = [{ stepId: rule.steps[0].id, tx }];
        stepIdx = 1;
      }
    }
  }
  return groups;
}
function finalizeGroup(rule, key, matches) {
  const chainIds = [...new Set(matches.map((m) => m.tx.chainId))];
  if (rule.crossChain && chainIds.length < 2) return null;
  const times = matches.map((m) => m.tx.timestamp);
  const spanMs = Math.max(...times) - Math.min(...times);
  return { ruleId: rule.id, key, matches, chainIds, spanMs };
}
function evaluateRules(rules, txs) {
  return rules.flatMap((rule) => evaluateRule(rule, txs));
}

// src/grouping/roles.ts
var CANON_SOURCE = "source";
var CANON_DESTINATION = "destination";
var DEFAULT_COMPLETE_ROLES = [CANON_SOURCE, CANON_DESTINATION];
var SOURCE_SYNONYMS = /* @__PURE__ */ new Set(["source", "send", "origin"]);
var DESTINATION_SYNONYMS = /* @__PURE__ */ new Set(["destination", "receive", "dest"]);
var ROLE_RANK = {
  source: 0,
  send: 0,
  origin: 0,
  destination: 1,
  receive: 1,
  dest: 1
};
var UNKNOWN_ROLE_RANK = 2;
var ROLE_COMPLEMENT = {
  [CANON_SOURCE]: CANON_DESTINATION,
  [CANON_DESTINATION]: CANON_SOURCE
};
var OPEN_ENDED_SAGA_KINDS = /* @__PURE__ */ new Set([
  "trade.token_position",
  "pool.lifecycle",
  "token.launch",
  // A wallet goes on transferring to a venue, so the backend registers this
  // saga with `aggregating_completion` and pins no completion set. Left out
  // here, its group would be read as forever awaiting a counterpart, and every
  // exchange-transfer card would carry a badge saying a leg was missing.
  "flow.exchange_deposit"
]);
var OPEN_ENDED_SAGA_PREFIXES = ["trade."];
var OPEN_ENDED_RELATIONSHIP_TYPES = /* @__PURE__ */ new Set(["trade"]);
function canonRole(role) {
  const value = (role ?? "").trim().toLowerCase();
  if (SOURCE_SYNONYMS.has(value)) return CANON_SOURCE;
  if (DESTINATION_SYNONYMS.has(value)) return CANON_DESTINATION;
  return value;
}
function roleRank(role) {
  return ROLE_RANK[(role ?? "").trim().toLowerCase()] ?? UNKNOWN_ROLE_RANK;
}
var ROLE_LABELS = {
  // A token's launch — `commons.grouping.token_launch`.
  token_deploy: "Token deployed",
  // A pool's lifecycle — `commons.grouping.pools`. Shared with the launch above.
  pool_open: "Pool created",
  pool_priced: "Pool priced",
  liquidity_add: "Liquidity added",
  liquidity_remove: "Liquidity removed",
  liquidity_move: "Liquidity changed",
  swap: "Traded",
  // A finding re-files its members under what the finding says they did.
  liquidity_pull: "Liquidity pulled",
  insider_buy: "Insider bought",
  // A JIT bracket (`pool.jit_front_run`). Deliberately not the sandwich's
  // `frontrun`/`backrun` wording, which the backend keeps for `mev.sandwich`
  // alone: those legs move the price and take the difference out of the trade
  // between them, whereas these add real depth for its duration.
  jit_open: "Liquidity added just in time",
  jit_close: "Liquidity pulled straight back",
  // The trade the bracket was built around. NOT "victim": the added depth is real
  // while this trade executes, so it slips LESS than the pool alone would have
  // made it. The party that pays is the pool's passive LPs, diluted out of the
  // fee on this flow — and they are positions, so no group member is them.
  target_swap: "Trade the liquidity was placed around",
  // Custody — `commons.grouping.exchange_flow`. Direction is the whole meaning
  // of these two, and the prettifier flattens it: "Exchange Deposit" and
  // "Exchange Withdrawal" are noun phrases that leave the reader to work out
  // which way the money went and whose it was.
  exchange_deposit: "Sent to the exchange",
  exchange_withdrawal: "Received from the exchange",
  // A forwarding chain. Written as "as far back as we could see" rather than
  // "source", which is the claim the detector cannot make: the walk stops at
  // its depth bound or at the first hop nobody ingested, so this wallet may
  // well have a funder of its own.
  chain_origin: "Earliest hop we can see",
  chain_hop: "Passed the funds on",
  chain_deposit: "Reached the exchange"
};
function roleLabel(role) {
  const canon = canonRole(role);
  if (!canon) return "";
  const known = ROLE_LABELS[canon];
  if (known) return known;
  const words = canon.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function isOpenEndedGroup(sagaKind, relationshipType) {
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (OPEN_ENDED_SAGA_KINDS.has(kind)) return true;
  if (OPEN_ENDED_SAGA_PREFIXES.some((prefix) => kind.startsWith(prefix))) return true;
  return OPEN_ENDED_RELATIONSHIP_TYPES.has((relationshipType ?? "").trim().toLowerCase());
}
function presentRoles(roles) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const role of roles) {
    const value = canonRole(role);
    if (value && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}
function expectedMissingRoles(rolesPresent, completeRoles) {
  const present = rolesPresent.filter((r) => r).map((r) => canonRole(r));
  const presentSet = new Set(present.filter((r) => r));
  let candidates = [];
  if (completeRoles && completeRoles.length > 0) {
    candidates = completeRoles.filter((r) => r).map((r) => canonRole(r));
  } else {
    for (const role of presentSet) {
      const complement = ROLE_COMPLEMENT[role];
      if (complement) candidates.push(complement);
    }
    if (candidates.length === 0) candidates = [...DEFAULT_COMPLETE_ROLES];
  }
  const seen = /* @__PURE__ */ new Set();
  const expected = [];
  for (const role of [...present, ...candidates]) {
    if (role && !seen.has(role)) {
      seen.add(role);
      expected.push(role);
    }
  }
  return { expected, missing: expected.filter((role) => !presentSet.has(role)) };
}

// src/grouping/sagas.ts
var SAGA_LABELS = {
  "bridge.ccip": "Chainlink CCIP",
  "bridge.cctp": "Circle CCTP",
  "bridge.wormhole": "Wormhole",
  // The rest of the bundled bridges, which fell through to the generic
  // prettify and so introduced themselves as "Bridge Layerzero V2" and
  // "Bridge Ibc" — the namespace leaked into the name and the protocol's own
  // capitalisation was lost. A card naming a protocol should spell it the way
  // the protocol does.
  //
  // Both LayerZero generations are listed and both say which: they are
  // different rails with different contracts and different chain numbering,
  // and a group card that called them both "LayerZero" would leave a reader
  // unable to tell which index to go and look in.
  "bridge.layerzero_v1": "LayerZero V1",
  "bridge.layerzero_v2": "LayerZero V2",
  "bridge.across_v3": "Across",
  "bridge.hyperlane": "Hyperlane",
  "bridge.mayan": "Mayan",
  "bridge.hop": "Hop",
  "bridge.ibc": "IBC",
  "trade.token_position": "Trading position",
  "token.launch": "Token launch",
  "pool.lifecycle": "Pool lifecycle",
  "pool.rug": "Rug pull",
  "pool.jit_front_run": "JIT front-run",
  "mev.sandwich": "Sandwich",
  "launch.insider_buy": "Insider buy",
  // Custody. "Exchange transfers" rather than "Exchange deposits" because the
  // group holds both directions — the saga kind names the common case, the
  // group does not.
  "flow.exchange_deposit": "Exchange transfers",
  // NOT "Peel chain", which is the technique's name among investigators and
  // means nothing to anybody else. What the finding says is that money moved
  // through a series of wallets and stopped somewhere it can no longer be
  // followed.
  //
  // "somewhere" rather than "an exchange" because the detector's terminus is a
  // CLASS now — an exchange, a custodial service, a bridge escrow, a mixer, a
  // sanctioned address. A static label naming one of them would be wrong for
  // the other four; `peelChainLabel` below narrows it when the group's payload
  // says which.
  "flow.peel_chain": "Funds forwarded through intermediaries",
  // NOT "Dust campaign", for the same reason the peel chain is not called one:
  // "dusting" is the technique's name among investigators and says nothing to
  // the person whose wallet is full of it. What the finding says is that one
  // address sprayed worthless transfers at a great many wallets.
  "spam.dust_campaign": "Bulk unsolicited transfers",
  approve_spend: "Approve / spend",
  userop_retry: "User-op retry"
};
var GROUP_RELATIONSHIP_LABELS = {
  cross_chain_message: "Cross-chain",
  intent_fulfillment: "Cross-chain",
  approve_spend: "Approve / spend",
  userop_retry: "Retry",
  entity_flow: "Entity flow",
  trade: "Position",
  pool_lifecycle: "Pool activity",
  token_launch: "Token launch",
  exchange_deposit: "Exchange flow",
  // The finding categories. They were bare strings on the detector's writer
  // until the enum learned them, so every one of them landed on the "Linked"
  // fallback below — which reads as "we found a relationship but can't say
  // what", about the groups that exist precisely because something was named.
  jit_liquidity: "JIT liquidity",
  mev_sandwich: "Sandwich",
  liquidity_rug: "Rug pull",
  insider_buy: "Insider buy",
  peel_chain: "Forwarded through wallets",
  custom: "Linked"
};
function groupRelationshipLabel(relationshipType) {
  const key = (relationshipType ?? "").trim().toLowerCase();
  return GROUP_RELATIONSHIP_LABELS[key] ?? "Linked";
}
var FINDING_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.rug",
  "pool.jit_front_run",
  "mev.sandwich",
  "launch.insider_buy",
  // The `flow.` namespace splits the same way `pool.` does: a wallet's
  // transfers to an exchange are ordinary activity, while funds arriving there
  // through a run of intermediate wallets is a detector's claim about them.
  "flow.peel_chain"
  // NOTE: `spam.dust_campaign` is deliberately NOT here, though a detector is
  // what recognises it and `tx_grouping.config.policy.FINDING_SAGA_KINDS` does
  // list it. That set decides SCOPE; this one decides how the group renders,
  // and a finding card enumerates the legs that make the case — three for a
  // sandwich, four for a peel chain. A campaign has forty thousand. It is in
  // ACTIVITY_SAGA_KINDS, matching `commons/grouping/shapes.py`.
]);
var TERMINUS_PHRASE = {
  exchange: "an exchange",
  cefi: "a custodial service",
  bridge: "a bridge",
  mixer: "a mixer",
  sanctioned: "a sanctioned address"
};
function peelChainLabel(terminusClass) {
  const phrase = TERMINUS_PHRASE[(terminusClass ?? "").trim().toLowerCase()];
  if (phrase) return `Funds forwarded to ${phrase}`;
  return SAGA_LABELS["flow.peel_chain"] ?? "Funds forwarded through intermediaries";
}
function isFindingSaga(kind) {
  return FINDING_SAGA_KINDS.has((kind ?? "").trim().toLowerCase());
}
var POSITION_SAGA_KINDS = /* @__PURE__ */ new Set(["trade.token_position"]);
var ACTIVITY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.lifecycle",
  "token.launch",
  // One address's bulk dusting — the shape this set exists for, taken to its
  // extreme. A campaign's members number in the tens of thousands, so the only
  // useful card summarises and links out. See the note in FINDING_SAGA_KINDS on
  // why a detector-recognised group is nonetheless not a finding CARD.
  "spam.dust_campaign"
]);
var CUSTODY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "flow.exchange_deposit"
]);
function sagaKindsByShape() {
  const known = /* @__PURE__ */ new Set([
    ...Object.keys(SAGA_LABELS),
    ...FINDING_SAGA_KINDS,
    ...POSITION_SAGA_KINDS,
    ...ACTIVITY_SAGA_KINDS,
    ...CUSTODY_SAGA_KINDS
  ]);
  const order = [
    "finding",
    "crossChain",
    "activity",
    "position",
    "custody",
    "linked"
  ];
  const buckets = /* @__PURE__ */ new Map();
  for (const kind of known) {
    const category = groupCardCategory(kind, null);
    const list = buckets.get(category) ?? [];
    list.push(kind);
    buckets.set(category, list);
  }
  return order.map((category) => ({
    category,
    title: GROUP_CARD_TITLES[category],
    kinds: (buckets.get(category) ?? []).sort(
      (a, b) => sagaLabel(a).localeCompare(sagaLabel(b))
    )
  })).filter((bucket) => bucket.kinds.length > 0);
}
var GROUP_CARD_TITLES = {
  crossChain: "Cross-chain",
  finding: "Findings",
  position: "Trading position",
  activity: "Pool & token activity",
  custody: "Exchange transfers",
  linked: "Related transactions"
};
var GROUP_SHAPE_LABELS = {
  crossChain: "Cross-chain",
  finding: "Finding",
  position: "Position",
  activity: "Activity",
  custody: "Exchange",
  linked: "Linked"
};
var GROUP_SHAPE_MEANING = {
  crossChain: "Legs of one transfer on different chains, linked by a shared message id.",
  finding: "A detector recognised a pattern here \u2014 membership in this group is itself the finding.",
  position: "One wallet's buys and sells of one token.",
  activity: "Everything that has happened to one pool or token launch.",
  custody: "One wallet's transfers to and from a custodial venue.",
  linked: "Related transactions the engine linked without a more specific shape."
};
function groupShapeLabel(sagaKind, relationshipType) {
  return GROUP_SHAPE_LABELS[groupCardCategory(sagaKind, relationshipType)];
}
function groupShapeMeaning(sagaKind, relationshipType) {
  return GROUP_SHAPE_MEANING[groupCardCategory(sagaKind, relationshipType)];
}
var GROUP_STATE_MEANING = {
  open: "Watching for further legs.",
  partial: "Some legs have arrived; still watching for the rest.",
  completed: "Every expected leg arrived.",
  timed_out: "Stopped waiting for the missing leg. That is a guess about a leg that never arrived, not a verdict \u2014 a reprocess can still reopen this group if the leg turns up.",
  cancelled: "Deliberately closed. This does not reopen.",
  reorged: "A member transaction left the canonical chain. This does not reopen."
};
function groupStateMeaning(state) {
  return GROUP_STATE_MEANING[(state ?? "").trim().toLowerCase()] ?? "";
}
function isReconsiderableState(state) {
  const value = (state ?? "").trim().toLowerCase();
  return value === "open" || value === "partial" || value === "timed_out";
}
function isReopenableState(state) {
  return (state ?? "").trim().toLowerCase() === "timed_out";
}
function groupStateLabel(state) {
  const value = (state ?? "").trim().toLowerCase() || "open";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");
}
function groupCardCategory(sagaKind, relationshipType) {
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (kind) {
    if (FINDING_SAGA_KINDS.has(kind)) return "finding";
    if (POSITION_SAGA_KINDS.has(kind)) return "position";
    if (ACTIVITY_SAGA_KINDS.has(kind)) return "activity";
    if (CUSTODY_SAGA_KINDS.has(kind)) return "custody";
    if (kind.startsWith("bridge.")) return "crossChain";
    if (kind.startsWith("trade.")) return "position";
  }
  const relationship = (relationshipType ?? "").trim().toLowerCase();
  if (relationship === "cross_chain_message" || relationship === "intent_fulfillment") {
    return "crossChain";
  }
  if (relationship === "pool_lifecycle" || relationship === "token_launch") {
    return "activity";
  }
  if (relationship === "trade") return "position";
  if (relationship === "exchange_deposit") return "custody";
  if (relationship === "jit_liquidity" || relationship === "mev_sandwich" || relationship === "liquidity_rug" || relationship === "insider_buy" || relationship === "peel_chain") {
    return "finding";
  }
  return "linked";
}
function groupTransactionCount(group) {
  const txs = group.transaction_count;
  if (typeof txs === "number" && txs > 0) return txs;
  const members = group.members?.length;
  if (typeof members === "number" && members > 0) return members;
  const legs = group.member_count;
  return typeof legs === "number" && legs > 0 ? legs : 0;
}
function isSoleMemberActivity(group) {
  if (!isOpenEndedGroup(group.saga_kind, group.relationship_type)) return false;
  return groupTransactionCount(group) <= 1;
}
function compareGroupCards(a, b) {
  const soleA = isSoleMemberActivity(a);
  const soleB = isSoleMemberActivity(b);
  if (soleA !== soleB) return soleA ? 1 : -1;
  const sizeDelta = groupTransactionCount(b) - groupTransactionCount(a);
  if (sizeDelta !== 0) return sizeDelta;
  return (a.saga_kind ?? "").localeCompare(b.saga_kind ?? "");
}
function prettifyToken(value) {
  const words = value.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length === 0 ? value : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function sagaLabel(kind) {
  if (!kind) return "Cross-chain";
  return SAGA_LABELS[kind] ?? prettifyToken(kind);
}
var SAGA_ID_LABELS = {
  message_id: "Message ID",
  vaa_id: "VAA",
  sequence: "Sequence",
  nonce: "Nonce",
  // The tuple keys. A prettify would read "Packet Path" / "Deposit Key", which
  // is close enough to leave alone — but "Pathway" is what LayerZero calls it
  // and "Packet origin" is what the V2 spec's own comment calls the tuple, so
  // spelling them here costs one line and stops the surface inventing a name
  // for something the protocol already named.
  packet_path: "Packet pathway",
  packet_origin: "Packet origin",
  deposit_key: "Deposit",
  transfer_id: "Transfer ID",
  packet_id: "Packet"
};
function sagaIdLabel(idLabel) {
  const key = (idLabel ?? "").trim().toLowerCase();
  if (!key) return "Correlation ID";
  return SAGA_ID_LABELS[key] ?? prettifyToken(key);
}
function titleRole(role) {
  return role.length ? role.charAt(0).toUpperCase() + role.slice(1) : role;
}
function sagaKeyOf(ref) {
  const kind = (ref.sagaKind ?? "").trim().toLowerCase();
  const value = (ref.value ?? "").trim().toLowerCase();
  if (!kind || !value) return null;
  return `${kind}:${value}`;
}
function compareSagas(a, b) {
  if (a.awaiting !== b.awaiting) return a.awaiting ? -1 : 1;
  if (a.legs.length !== b.legs.length) return b.legs.length - a.legs.length;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}
function sagasFromRefs(entries, rowKey) {
  const buckets = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    const key = rowKey(entry.row);
    for (const ref of entry.refs) {
      const sagaKey = sagaKeyOf(ref);
      if (!sagaKey) continue;
      let bucket = buckets.get(sagaKey);
      if (!bucket) {
        const sagaKind = (ref.sagaKind ?? "").trim().toLowerCase();
        bucket = {
          seen: /* @__PURE__ */ new Set(),
          saga: {
            key: sagaKey,
            sagaKind,
            value: (ref.value ?? "").trim().toLowerCase(),
            protocolId: ref.protocolId,
            idLabel: ref.idLabel,
            linkageType: ref.linkageType,
            linkageModel: ref.linkageModel,
            relationshipType: ref.relationshipType,
            legs: [],
            rolesPresent: [],
            expectedRoles: [],
            missingRoles: [],
            awaiting: false,
            complete: false,
            openEnded: isOpenEndedGroup(sagaKind, ref.relationshipType)
          }
        };
        buckets.set(sagaKey, bucket);
      }
      if (!bucket.seen.has(key)) {
        bucket.seen.add(key);
        const role = canonRole(ref.role);
        bucket.saga.legs.push({
          row: entry.row,
          role,
          rawRole: ref.role && ref.role !== role ? ref.role : void 0,
          ref
        });
      }
    }
  }
  for (const { saga } of buckets.values()) {
    saga.legs.sort((a, b) => roleRank(a.role) - roleRank(b.role));
    const declared = saga.legs.map((leg) => leg.ref.completeRoles).find((r) => r && r.length > 0);
    const roles = saga.legs.map((leg) => leg.role);
    const { expected, missing } = expectedMissingRoles(roles, declared);
    saga.rolesPresent = [...new Set(roles.filter(Boolean))];
    saga.expectedRoles = expected;
    saga.missingRoles = saga.openEnded ? [] : missing;
    saga.complete = saga.missingRoles.length === 0;
    saga.awaiting = !saga.openEnded && !saga.complete;
  }
  const sagas = [...buckets.values()].map((b) => b.saga).sort(compareSagas);
  return { sagas, byRow: rowStatesFromSagas(sagas, rowKey) };
}
function hashesOf(group) {
  const out = /* @__PURE__ */ new Set();
  for (const member of group.members ?? []) {
    const hash = (member.tx_hash ?? "").trim().toLowerCase();
    if (hash) out.add(hash);
  }
  return out;
}
function reconcileSagas(sagas, groupsFor, txHashOf) {
  if (sagas.length === 0) return [];
  const out = sagas.map((saga) => {
    const legHashes = new Set(
      saga.legs.map((leg) => (txHashOf(leg.row) ?? "").trim().toLowerCase()).filter(Boolean)
    );
    let best;
    let bestOverlap = 0;
    const seen = /* @__PURE__ */ new Set();
    for (const leg of saga.legs) {
      for (const group of groupsFor(leg.row)) {
        if (seen.has(group.group_id)) continue;
        seen.add(group.group_id);
        if (saga.sagaKind && group.saga_kind && group.saga_kind !== saga.sagaKind) continue;
        let overlap = 0;
        for (const hash of hashesOf(group)) if (legHashes.has(hash)) overlap += 1;
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          best = group;
        }
      }
    }
    if (!best) return saga;
    const missing = [...best.missing_roles ?? []];
    const expected = best.expected_roles?.length ? [...best.expected_roles] : saga.expectedRoles;
    const openEnded = best.open_ended ?? saga.openEnded;
    return {
      ...saga,
      expectedRoles: expected,
      missingRoles: openEnded ? [] : missing,
      awaiting: openEnded ? false : best.awaiting ?? missing.length > 0,
      complete: openEnded || missing.length === 0,
      openEnded,
      confirmed: true,
      groupId: best.group_id,
      groupState: best.state ?? void 0,
      memberCount: best.member_count ?? void 0
    };
  });
  return out.sort(compareSagas);
}
function rowStatesFromSagas(sagas, rowKey) {
  const byRow = /* @__PURE__ */ new Map();
  for (const saga of sagas) {
    for (const leg of saga.legs) {
      const key = rowKey(leg.row);
      const list = byRow.get(key);
      if (list) list.push(saga);
      else byRow.set(key, [saga]);
    }
  }
  const out = /* @__PURE__ */ new Map();
  for (const [key, rowSagas] of byRow) {
    const roles = new Set(
      rowSagas.flatMap((s) => s.legs.filter((l) => rowKey(l.row) === key).map((l) => l.role)).filter(Boolean)
    );
    out.set(key, {
      sagas: rowSagas,
      complete: rowSagas.filter((s) => s.complete).length,
      total: rowSagas.length,
      missingRoles: [...new Set(rowSagas.flatMap((s) => s.missingRoles))],
      awaiting: rowSagas.some((s) => s.awaiting),
      role: roles.size === 1 ? [...roles][0] : void 0,
      sagaKind: rowSagas[0]?.sagaKind,
      protocolId: rowSagas[0]?.protocolId,
      confirmed: rowSagas.every((s) => s.confirmed) ? true : void 0
    });
  }
  return out;
}

export { ACTIVITY_SAGA_KINDS, CANON_DESTINATION, CANON_SOURCE, CUSTODY_SAGA_KINDS, DEFAULT_COMPLETE_ROLES, FINDING_SAGA_KINDS, GROUPING_COMPARATORS, GROUPING_SCOPES, GROUP_CARD_TITLES, GROUP_RELATIONSHIP_LABELS, GROUP_SHAPE_LABELS, GROUP_SHAPE_MEANING, GROUP_STATE_MEANING, GroupingRuleSchema, OPEN_ENDED_RELATIONSHIP_TYPES, OPEN_ENDED_SAGA_KINDS, OPEN_ENDED_SAGA_PREFIXES, POSITION_SAGA_KINDS, ROLE_COMPLEMENT, ROLE_LABELS, ROLE_RANK, SAGA_ID_LABELS, SAGA_LABELS, UNKNOWN_ROLE_RANK, approveThenSendRule, canonRole, compareGroupCards, compareSagas, evaluateRule, evaluateRules, expectedMissingRoles, groupCardCategory, groupRelationshipLabel, groupShapeLabel, groupShapeMeaning, groupStateLabel, groupStateMeaning, groupTransactionCount, isFindingSaga, isOpenEndedGroup, isReconsiderableState, isReopenableState, isSoleMemberActivity, peelChainLabel, presentRoles, prettifyToken, reconcileSagas, roleLabel, roleRank, rowStatesFromSagas, sagaIdLabel, sagaKindsByShape, sagaLabel, sagasFromRefs, stepMatches, titleRole, validateGroupingRule };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map